import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

const STORAGE_KEY = 'mori.adsOptIn'

function readFromStorage(): boolean | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return undefined
    return raw === '1'
  } catch {
    return undefined
  }
}

function writeToStorage(value: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, value ? '1' : '0')
  } catch {
    // ignore
  }
}

export function useAdsPersonalization() {
  const { user } = useAuth()
  const userId = user?.id
  const qc = useQueryClient()

  const storedValue = readFromStorage()

  const query = useQuery({
    queryKey: ['ads-personalization', userId],
    enabled: !!userId,
    // Seed from localStorage so first render skips isLoading when cached
    initialData: storedValue !== undefined ? storedValue : undefined,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('personalized_ads_opt_in')
        .eq('id', userId!)
        .single()
      if (error) throw error
      const value = data.personalized_ads_opt_in as boolean
      writeToStorage(value)
      return value
    },
  })

  const setMutation = useMutation({
    mutationFn: async (value: boolean) => {
      const { error } = await supabase
        .from('profiles')
        .update({ personalized_ads_opt_in: value })
        .eq('id', userId!)
      if (error) throw error
      return value
    },
    onMutate: async (newValue: boolean) => {
      await qc.cancelQueries({ queryKey: ['ads-personalization', userId] })
      const previous = qc.getQueryData<boolean>(['ads-personalization', userId])
      qc.setQueryData(['ads-personalization', userId], newValue)
      writeToStorage(newValue)
      return { previous }
    },
    onError: (_err, _value, context) => {
      if (context?.previous !== undefined) {
        qc.setQueryData(['ads-personalization', userId], context.previous)
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ads-personalization', userId] })
    },
  })

  if (!userId) {
    return {
      data: undefined as boolean | undefined,
      isLoading: false,
      isError: false,
      error: null,
      refetch: () => Promise.resolve({ data: undefined }),
      optedIn: false,
      setOptedIn: (_value: boolean) => {},
    }
  }

  const optedIn = query.data ?? false

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
    optedIn,
    setOptedIn: (value: boolean) => setMutation.mutate(value),
  }
}
